import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import { Link } from 'gatsby';
import '../design/design-guidelines-styles.css';
import { Button, Paragraph, Heading } from 'react-magma-dom';
import { LeadParagraph } from '../../components/LeadParagraph';
export const _frontmatter = {
  "pageTitle": "Typography Design Guidelines",
  "title": "Typography",
  "order": 2
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

<PageContent componentName="typography" type="design_intro" mdxType="PageContent">
<LeadParagraph mdxType="LeadParagraph">
  Cengage applications need clear and consistent headings, highly legible body
  paragraphs, and clear labels. Our typefaces are chosen for legibility and
  maximum support for mathematical characters.
</LeadParagraph>
<LeadParagraph mdxType="LeadParagraph">
  By consistently tying typographic styles to appropriate functions in the
  interface, we create a clear visual pattern for students and faculty to follow
  while they’re interacting with our products.
</LeadParagraph>
<hr></hr>
<h2>{`Magma Type Sets`}</h2>
<p>{`Magma has three sets of ready-made typographic elements that you can choose from and should be adhered to when at all possible. Each one combines a font family with calibrated sizes and weights to work within applications or editorial content. The three sets are called `}<strong parentName="p">{`Productive`}</strong>{`, `}<strong parentName="p">{`Expressive`}</strong>{`, and `}<strong parentName="p">{`Narrative`}</strong>{`.`}</p>
<p><em parentName="p"><strong parentName="em">{`IMPORTANT:`}</strong>{` The Productive set is used by default in the components so you won't actually see it referred to in the code. You can easily change to Expressive or Narrative when appropriate by adding the necessary prop.`}</em></p>
<h3>{`Productive Type Set (Default)`}</h3>
<p>{`Productive type is reserved for use in web-based product design, where the user needs to focus on tasks. The Productive styles are curated to create a series of clear user expectations about hierarchy. The difference between the Productive and Expressive styles is mainly evident in the Headings.`}</p>
<h4>{`Productive Body Text Styles`}</h4>
<div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="bodyLarge" mdxType="Paragraph">
        This is a good size for higher emphasis text, as well as the subheadline
        of a page.
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="bodyMedium" mdxType="Paragraph">
        This is commonly used in layouts for paragraphs. The line height and
        size make for comfortable, long-form reading, in mediums that allow for
        more space. This size type is the basis for how all other text is
        calculated.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / Medium</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 16px / 1em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="bodySmall" mdxType="Paragraph">
        This is commonly used for helper and error text on inputs, as well as
        other explanatory text.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / Small</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 14px / .875em</p>
      <p>Line-height: 20px / 1.25em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: .16px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="bodyXSmall" mdxType="Paragraph">
        This is good to use for captions and other meta-data or explanatory
        text.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / X-Small</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 12px / .75em</p>
      <p>Line-height: 16px / 1em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: .32px</p>
    </div>
  </div>
</div>
<span className="horizontal-spacer"></span>
<h4>{`Productive Heading Text Styles`}</h4>
<div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="headingXLarge" mdxType="Paragraph">
        This is used for layout headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 36px / 2.25em</p>
      <p>Line-height: 48px / 3em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="headingLarge" mdxType="Paragraph">
        This is used for layout headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="headingMedium" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="headingSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="headingXSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Heading / X-Small</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph noMargins visualStyle="heading2XSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Heading / 2X-Small</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 12px / .75em</p>
      <p>Line-height: 16px / 1em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: .32px</p>
    </div>
  </div>
</div>
<span className="horizontal-spacer"></span>
<h3>{`Expressive Type Set`}</h3>
<p>{`Expressive type is reserved for use in editorial and digital marketing experiences which may require more dynamic typographic hierarchies and more variety in the Heading scale. The Expressive styles are curated to create a series of clear user expectations while still allowing for a more dramatic, graphic use of type.`}</p>
<h4>{`Expressive Body Text Styles`}</h4>
<div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="bodyLarge" mdxType="Paragraph">
        This is a good size for higher emphasis text, as well as the subheadline
        of a page.
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 20px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
      <p>Color: primary600 / #292F7C</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
      <p>Color: primary600 / #292F7C</p>
    </div>
  </div>
</div>
<span className="horizontal-spacer"></span>
<h4>{`Expressive Heading Text Styles`}</h4>
<div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="heading2XLarge" mdxType="Paragraph">
        This is used for layout headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / 2X-Large</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 64px / 4em</p>
      <p>Line-height: 84px / 5.25em</p>
      <p>Weight: Medium / 500</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / 2X-Large</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 48px / 3em</p>
      <p>Line-height: 64px / 4em</p>
      <p>Weight: Medium / 500</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="headingXLarge" mdxType="Paragraph">
        This is used for layout headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 48px / 3em</p>
      <p>Line-height: 64px / 4em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 36px / 2.25em</p>
      <p>Line-height: 48px / 3em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="headingLarge" mdxType="Paragraph">
        This is used for layout headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 36px / 2.25em</p>
      <p>Line-height: 48px / 3em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="headingMedium" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Semibold / 600</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="headingSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Medium / 500</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Medium / 500</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="headingXSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / X-Small</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Medium / 500</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / X-Small</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 18px / 1.25em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Medium / 500</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="expressive" noMargins visualStyle="heading2XSmall" mdxType="Paragraph">
        This is used for layout headings and component titles
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / 2X-Small</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Medium / 500</p>
      <p>Letter-spacing: .32px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / 2X-Small</strong>
      </p>
      <p>Typeface: Work Sans</p>
      <p>Size: 16px / 1em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Medium / 500</p>
      <p>Letter-spacing: .32px</p>
    </div>
  </div>
</div>
<span className="horizontal-spacer"></span>
<h3>{`Narrative Type Set`}</h3>
<p>{`Narrative type uses Noto Serif but is otherwise styled the same as the default Productive variant. The Narrative styles are curated to create a series of clear user expectations about hierarchy, but in a font that is more pleasurable for reading long passages of text.`}</p>
<h4>{`Narrative Body Text Styles`}</h4>
<div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="bodyLarge" mdxType="Paragraph">
        This is a good size for higher emphasis text, as well as the subheadline
        of a page.
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Body / Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="bodyMedium" mdxType="Paragraph">
        This is commonly used in layouts for paragraphs. The line height and
        size make for comfortable, long-form reading, in mediums that allow for
        more space. This size type is the basis for how all other text is
        calculated.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / Medium</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 16px / 1em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="bodySmall" mdxType="Paragraph">
        This size is great for creating contrast between the main paragraph of
        the passage and secondary information.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 14px / .875em</p>
      <p>Line-height: 20px / 1.25em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: .16px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="bodyXSmall" mdxType="Paragraph">
        This is good to use for captions and other meta-data or explanatory
        text.
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Body / X-Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 12px / .75em</p>
      <p>Line-height: 16px / 1em</p>
      <p>Weight: Regular / 400</p>
      <p>Letter-spacing: .32px</p>
    </div>
  </div>
</div>
<span className="horizontal-spacer"></span>
<h4>{`Narrative Heading Text Styles`}</h4>
<div className="type-details-table">
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="headingXLarge" mdxType="Paragraph">
        This is commonly used for H1 headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 36px / 2.25em</p>
      <p>Line-height: 48px / 3em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / X-Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="headingLarge" mdxType="Paragraph">
        This is commonly used for H2 headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 28px / 1.75em</p>
      <p>Line-height: 40px / 2.5em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Large</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="headingMedium" mdxType="Paragraph">
        This is commonly used for H3 headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 24px / 1.5em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Medium</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="headingSmall" mdxType="Paragraph">
        This is commonly used for H4 headings
      </Paragraph>
    </div>
    <div className="type-specs specs-desktop">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 20px / 1.25em</p>
      <p>Line-height: 32px / 2em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
    <div className="type-specs specs-mobile">
      <p>
        <strong>Heading / Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="headingXSmall" mdxType="Paragraph">
        This is commonly used for H5 headings
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Heading / X-Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 18px / 1.125em</p>
      <p>Line-height: 24px / 1.5em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: 0px</p>
    </div>
  </div>
  <div className="type-example">
    <div className="type-sample">
      <Paragraph contextVariant="narrative" noMargins visualStyle="heading2XSmall" mdxType="Paragraph">
        This is commonly used for H6 headings
      </Paragraph>
    </div>
    <div className="type-specs">
      <p>
        <strong>Heading / 2X-Small</strong>
      </p>
      <p>Typeface: Noto Serif</p>
      <p>Size: 12px / .75em</p>
      <p>Line-height: 16px / 1em</p>
      <p>Weight: Bold / 700</p>
      <p>Letter-spacing: .32px</p>
    </div>
  </div>
</div>
<span className="horizontal-spacer"></span>
<h3>{`Changing Headings Styles`}</h3>
<p>{`The headings presets above are assigned by default to H1 through H6 in the `}<Link to="/api/heading/" mdxType="Link">{`Headings component`}</Link>{`. But it's relatively common to need a specific heading type in the markup, but not want the default styling. For this reason we have made it very easy to change which styling preset you use in the headings component.`}</p>
<p>{`One example of this is in the `}<Link to="/api/modal/" mdxType="Link">{`Modal component`}</Link>{`. For optimal accessibility the title of the modal needs to be an H1, but we definitely don't want it to be styled with text that large. So we use an H1, but apply the Productive Heading Small style to it. This flexibility allows consumers of Magma to design using the styles that work best for the UI, but still have a clear informational hierarchy for assistive technologies.`}</p>
<figure>{`
  `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1035px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "43.62934362934363%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAJCAYAAAAywQxIAAAACXBIWXMAAAsTAAALEwEAmpwYAAABkklEQVQoz6WPu0oDQRSG9w0sbEUsLLVXBH0AkRRCDJJA0N0NlormEcTGRmxExNZSSPIWCdEE02QzcRNxQy6Ku7nsbeZ3ZoyX2Fj4w8dh5pz55z+Krqk0lUp9o+tUU1WJusvrL+Sd6GvaF7ouKu+pGlWOzy9BjCruSyWUOQ+VCgxCUHusgzRN1MaQH4ieYRhjqiDExO1tBolEHMrJ1Q1jgc/a7Q7rdLus99xiGAYcj4XWC6Ot10msVwZ7yABMcHdfZvF4nClnF9cQ6nZ7cPp9UM9HwHlKnsKc3kZjbheNmSQasztozuswp7ZgrhzC429oEML3ffm+WCzyhAko2WwWlFL0udlgMIA7GkGMNNKXqC3sob56BLJ8ALK0j/paGmSR38WOMWQBfNeD67rSsFAogCf8MBSybRue5yEMQ8nQ6cNu89QdwcsH4swZvNlyJgiCr4TCMBqNQsnlctLIsiw4jiObIvFfYoxJhLFQPp9HJBL5TihWFvE/f/2T8ZwI82kYi25ByWQy+I9ESrlyvoD1zQ28A2S4Z1yJM0C6AAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "",
            "title": "",
            "src": "/version/3.11.0/static/549e2c69eed42df74faddd4975015c44/e3189/modal-title-style.png",
            "srcSet": ["/version/3.11.0/static/549e2c69eed42df74faddd4975015c44/a2ead/modal-title-style.png 259w", "/version/3.11.0/static/549e2c69eed42df74faddd4975015c44/6b9fd/modal-title-style.png 518w", "/version/3.11.0/static/549e2c69eed42df74faddd4975015c44/e3189/modal-title-style.png 1035w", "/version/3.11.0/static/549e2c69eed42df74faddd4975015c44/4352a/modal-title-style.png 1364w"],
            "sizes": "(max-width: 1035px) 100vw, 1035px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
</figure>
<h4>{`Consistency is Key`}</h4>
<p>{`Continuing from above, there isn't just one way an H1 or and H3 should be styled. We have chosen logical default styles, but it's easy to say you need an H3 to be styled using one of the larger or smaller presets. The key is to be consistent. If you style a page title using productive-heading-large, then you need to use the same styling on all of the pages. If you use "productive-heading-xsmall" to style the title of a section, then you should use the same styling on equivalent sections.`}</p>
<h3>{`Responsive Behavior`}</h3>
<p>{`Another advantage to using the presets above is that Responsive behavior is already built into each style so you don't have to figure out and include it yourself. This means a number of the larger sizes will automatically reduce in size at 600px wide. This ensures we have appropriate text sizes on smaller devices. The breakpoint can be overridden if you want this change to happen earlier or later.`}</p>
<figure>{`
  `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1035px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "47.87644787644788%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAKCAIAAAA7N+mxAAAACXBIWXMAAAsTAAALEwEAmpwYAAABIElEQVQoz42Ry2qDYBCFfV2zEJS6z1Npda8LFVGEeL9Voi4SkoKpl2rt6W8adNGSbyHD4JxzZn6qbdsPwqN4Hqrv+y/CMAzTNH2uGP4FP1AQOBwOYRhmWRZF0TzPEJqfg4LG+Xw+nU632+1yufQENCGqKIogCK8EFKIoSpIkyzJqx3Huzm8EOOd5jm+apsfjUVVVjuN4nn8h0DS92+3QYVmWYZj9fu95HgXDdEuSJNDSdd33/bquq6qCFs6JRF3XwQyBLcsyDOPPYdM0EQQFloIEFlnOMY4jCtd178PRFhwPEnCGCsYwXxRF9guayGLbtqZpP0/1vuV6vTZNA5UgCJa/wSNXHMdlWeJgaFJLjDXLU8ETEWC7nlyWwgnwuljnG0m1FdRD1PN8AAAAAElFTkSuQmCC')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "",
            "title": "",
            "src": "/version/3.11.0/static/a423b000687c7ea4fb1c39f230b88821/e3189/responsive-example.png",
            "srcSet": ["/version/3.11.0/static/a423b000687c7ea4fb1c39f230b88821/a2ead/responsive-example.png 259w", "/version/3.11.0/static/a423b000687c7ea4fb1c39f230b88821/6b9fd/responsive-example.png 518w", "/version/3.11.0/static/a423b000687c7ea4fb1c39f230b88821/e3189/responsive-example.png 1035w", "/version/3.11.0/static/a423b000687c7ea4fb1c39f230b88821/f793b/responsive-example.png 1404w"],
            "sizes": "(max-width: 1035px) 100vw, 1035px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
</figure>
<hr></hr>
<h2>{`Typefaces`}</h2>
<h3>{`Work Sans`}</h3>
<p>{`Our primary typface is Work Sans. It’s clear and approachable and is great for headlines, subheads, navigation and body copy.`}</p>
<undefined><a {...{
          "href": "https://fonts.google.com/specimen/Work+Sans",
          "target": "_blank"
        }}>{`
  Download Work Sans
`}</a>
<div {...{
          "className": "two-col"
        }}>{`
  `}<div parentName="div" {...{
            "className": "row"
          }}>{`
    `}<div parentName="div" {...{
              "className": "col col-1"
            }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                  "className": "gatsby-resp-image-wrapper",
                  "style": {
                    "position": "relative",
                    "display": "block",
                    "marginLeft": "auto",
                    "marginRight": "auto",
                    "maxWidth": "756px"
                  }
                }}>{`
      `}<span parentName="span" {...{
                    "className": "gatsby-resp-image-background-image",
                    "style": {
                      "paddingBottom": "66.02316602316603%",
                      "position": "relative",
                      "bottom": "0",
                      "left": "0",
                      "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABcElEQVQoz32SO4vCUBCF83MNaGuhPyONYiMYCYiglSiIYCGIrYimEKPZ+CIKQozxmf28d9cVXXOKyZl7Z+bMzI1y+MXtdjsej77vw/f7ved52EMkFPkJgiCTyZimSQnysWEYXi4XWetjMuWJGwwGsVhM13U4J51Op1AoDIfD8/kckf+TnM/n+/1+LpfbbDaLxaJUKnW73XQ6jfuY5Z/k0+nkuq6qquVyOZFINJtNarVarWq1mkwml8tlhLhCaKVSyWazNFmr1TRN6/V6qVRqNBrF4/HxeBwxucJdo9Gg1VCgXq9PJpNisWgYBrO02+3r9foxmQsaY9ueAJwhqQihFhG+wPOzPWrdF2bb9nQ6/RKYzWbMaQtYloXlBLJarRzHIQy7Xq8Ro8Q9mWh8R4Ag3Pl8jpUuHEsAJ7vdTjbyp4wgFyhIfdS22y2PRAJWclfg5Z+7zyyVUUAKjg4D84RyfgCXlm5ff8/gDf4THkt63/k3xwTF3s58cG0AAAAASUVORK5CYII=')",
                      "backgroundSize": "cover",
                      "display": "block"
                    }
                  }}></span>{`
  `}<img parentName="span" {...{
                    "className": "gatsby-resp-image-image",
                    "alt": "",
                    "title": "",
                    "src": "/version/3.11.0/static/5360dd4507d4bbf4702efc048faa113e/8ae3e/work-sans-characters.png",
                    "srcSet": ["/version/3.11.0/static/5360dd4507d4bbf4702efc048faa113e/a2ead/work-sans-characters.png 259w", "/version/3.11.0/static/5360dd4507d4bbf4702efc048faa113e/6b9fd/work-sans-characters.png 518w", "/version/3.11.0/static/5360dd4507d4bbf4702efc048faa113e/8ae3e/work-sans-characters.png 756w"],
                    "sizes": "(max-width: 756px) 100vw, 756px",
                    "style": {
                      "width": "100%",
                      "height": "100%",
                      "margin": "0",
                      "verticalAlign": "middle",
                      "position": "absolute",
                      "top": "0",
                      "left": "0"
                    },
                    "loading": "lazy"
                  }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
              "className": "col col-2"
            }}>{`
      Work Sans is a typeface family based loosely on early Grotesques, such as
      those by Stephenson Blake, Miller & Richard and Bauerschen Giesserei. The
      Regular weight and others in the middle of the family are optimised for
      on-screen text usage at medium-sizes (14px-48px) and can also be used in
      print design. The fonts closer to the extreme weights are designed more
      for display use both on the web and in print. Overall, features are
      simplified and optimised for screen resolutions; for example, diacritic
      marks are larger than how they would be in print. A version optimised for
      desktop applications is available from the Work Sans github project page.
    `}</div>{`
  `}</div>
</div></undefined>
<h3>{`Noto Serif`}</h3>
<p>{`Noto Serif is a beautiful serif font. It's easy to read, provides a nice contrast with Work Sans and has great support for special characters. At this point, Noto Serif should primarily be used for the reader applications that display our text books.`}</p>
<undefined><a {...{
          "href": "https://fonts.google.com/specimen/Noto+Serif",
          "target": "_blank"
        }}>{`
  Download Noto Serif
`}</a>
<div {...{
          "className": "two-col"
        }}>{`
  `}<div parentName="div" {...{
            "className": "row"
          }}>{`
    `}<div parentName="div" {...{
              "className": "col col-1"
            }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                  "className": "gatsby-resp-image-wrapper",
                  "style": {
                    "position": "relative",
                    "display": "block",
                    "marginLeft": "auto",
                    "marginRight": "auto",
                    "maxWidth": "756px"
                  }
                }}>{`
      `}<span parentName="span" {...{
                    "className": "gatsby-resp-image-background-image",
                    "style": {
                      "paddingBottom": "66.02316602316603%",
                      "position": "relative",
                      "bottom": "0",
                      "left": "0",
                      "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABcUlEQVQoz32Sy4rCYAyF+7r6FuIFXOnWjSsVCqLoxhuIrnXnQooy9VrBFkGxVev188+MDog9i5A/OSdJk2oHhdPpZNv2drs9Ho88Xdf1PE/s4Ts0od7v90gkks/ncfb7/fV6JX4+nykaoNfIXS6X+XyeSqXQE0LZarWy2WyhUHAcJ0CvSdtKpTIYDGKxWL/f59ntdi3Lolyj0eAJ5+vY1E6n07VaLRqNZjIZ2OVyud1ux+PxZrMZJCbX6XSYEGc4HIZCofF4HA6HGTiRSJRKpSAxbXVdLxaLrMcwjGQy2ev16vV6LperVquMTfzrN5OQrboKbItrsUIit9sNX+Kc4PN+T/FkMjFNE8vOmXk2m2F/FCQ1Go2WyyVZU2GxWMgJntsmsVqtWO90OsUifjmkLAUI+LvdjobvzswDSag0REYHWwHBer3ebDaOAj5k+QXfYpRQpQQCeHyz7/vsAsuE/h9E+dL/3lmsgJz3D68+nzt/ADlpwZedqoUBAAAAAElFTkSuQmCC')",
                      "backgroundSize": "cover",
                      "display": "block"
                    }
                  }}></span>{`
  `}<img parentName="span" {...{
                    "className": "gatsby-resp-image-image",
                    "alt": "",
                    "title": "",
                    "src": "/version/3.11.0/static/f2a309f68dfda25e4170e7c5bb7adbd9/8ae3e/noto-serif-characters.png",
                    "srcSet": ["/version/3.11.0/static/f2a309f68dfda25e4170e7c5bb7adbd9/a2ead/noto-serif-characters.png 259w", "/version/3.11.0/static/f2a309f68dfda25e4170e7c5bb7adbd9/6b9fd/noto-serif-characters.png 518w", "/version/3.11.0/static/f2a309f68dfda25e4170e7c5bb7adbd9/8ae3e/noto-serif-characters.png 756w"],
                    "sizes": "(max-width: 756px) 100vw, 756px",
                    "style": {
                      "width": "100%",
                      "height": "100%",
                      "margin": "0",
                      "verticalAlign": "middle",
                      "position": "absolute",
                      "top": "0",
                      "left": "0"
                    },
                    "loading": "lazy"
                  }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
              "className": "col col-2"
            }}>{`
      `}<p parentName="div">{`
        When text is rendered by a computer, sometimes there will be characters
        in the text that can not be displayed, because no font that supports
        them is available to the computer. When this occurs, small boxes are
        shown to represent the characters. We call those small boxes “tofu,” and
        we want to remove tofu from the Web. This is how the Noto font families
        got their name.
      `}</p>{`
      `}<p parentName="div">{`
        Noto helps to make the web more beautiful across platforms for all
        languages. Currently, Noto covers over 30 scripts, and will cover all of
        Unicode in the future. This is the Serif Latin, Greek and Cyrillic
        family. It has Regular, Bold, Italic and Bold Italic styles and is
        hinted.
      `}</p>{`
    `}</div>{`
  `}</div>
</div></undefined>
<hr></hr>
<h2>{`Type Scale`}</h2>
<p>{`The font sizes used in the sets above come from the full type scale. This type scale is based on the Major Second Type Scale (1.125) but is slightly modified to round to whole numbers and create a simple pattern in the difference between each size. Small font sizes differ by 2px, and then that difference increases by 4px and then 6px as the size gets larger.`}</p>
<p>{`Line-heights are also defined at this level to adhere to an 8px spacing system, and should not be changed unless absolutely necessary`}</p>
<p>{`If you have a need to use a font size outside of the pre-made sets above, you may only choose from this list.`}</p>
<figure>{`
  `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1035px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "122.00772200772201%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAYCAIAAAB1KUohAAAACXBIWXMAAAsTAAALEwEAmpwYAAABx0lEQVQ4y6WU53LCMBCE9f5vxgw/TBt6GwjFFINNNyUf3kRgY5xkcjMch6S93TudMLc/2vXbiA1f8/n8crmsVqvT6fS8nTC2wjCcTCYfkd3BfHa7HeDD4bDZbPDXTAPPsS9mgcPICABny4aGY4rvYNSSCdh6vQ6CgCzZNceYdZp8lO37/na7zQZz4AGGUzUHkakFGeD9fh8D41mCWcqPx+NvmSWbJc/zkC3/jlzMj4Y9g2EWWHf+M1iyUTudTpfLJR6867rS9gqmKY8Jk0IuYLFYkMJWntp2XWoKGE4Ez2Yz+ElECp1OgG07jfqMh3A4HLqRDQYDlOPJksDHZNuGkQ8qpCIYJBk16q9g20sjwShHbbPZ7HQ6PDI8MUG32+UZPeM5GRtPGcwUDCEBJWjIIeFo4tpsIqNXRgRDsVhsNBr9fn80GhHXajXebavVKpfLFgMzeZPM5/MZTkgUUDwBPzX2qQNjNFv48Xicz+fb7XapVOLOYHYcp16vo6JSqVSrVd1osmZFVEsWNvQwKJs7Y4WL0XpKzfozwHNP9FZDApJWAyZgWsjLVq/XE3lsSPQDqegsFApI5RxV5HI5rah/iBfYtsDc/mGfHERy9YPqKTwAAAAASUVORK5CYII=')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "",
            "title": "",
            "src": "/version/3.11.0/static/872cc46d87d31a873b9a05c997e03677/e3189/type-scale.png",
            "srcSet": ["/version/3.11.0/static/872cc46d87d31a873b9a05c997e03677/a2ead/type-scale.png 259w", "/version/3.11.0/static/872cc46d87d31a873b9a05c997e03677/6b9fd/type-scale.png 518w", "/version/3.11.0/static/872cc46d87d31a873b9a05c997e03677/e3189/type-scale.png 1035w", "/version/3.11.0/static/872cc46d87d31a873b9a05c997e03677/f793b/type-scale.png 1404w"],
            "sizes": "(max-width: 1035px) 100vw, 1035px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
</figure>
<hr></hr>
<h2>{`Styles`}</h2>
<h3>{`Weight`}</h3>
<p>{`Font weight is an important typographic variable that can add emphasis and differentiate content hierarchy. Font weight and size pairings must be carefully balanced. A bold weight will always have more emphasis than a lighter weight font of the same size. However, a lighter weight font can rank hierarchically higher than a bold font if the lighter weight type size is significantly larger than the bold one. The weights available in React Magma are `}<inlineCode parentName="p">{`thin (100)`}</inlineCode>{`, `}<inlineCode parentName="p">{`extra-light (200)`}</inlineCode>{`, `}<inlineCode parentName="p">{`light (300)`}</inlineCode>{`, `}<inlineCode parentName="p">{`regular (400)`}</inlineCode>{`, `}<inlineCode parentName="p">{`medium (500)`}</inlineCode>{`, `}<inlineCode parentName="p">{`semi-bold (600)`}</inlineCode>{`, `}<inlineCode parentName="p">{`bold (700)`}</inlineCode>{`, `}<inlineCode parentName="p">{`extra-bold (800)`}</inlineCode>{` and `}<inlineCode parentName="p">{`black (900)`}</inlineCode>{`.`}</p>
<div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "118.91891891891892%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAYCAIAAAB1KUohAAAACXBIWXMAAAsTAAALEwEAmpwYAAACAUlEQVQ4y6WSR48aQRCF9/+fOfjGASHgQM4ZiRMiR5NEzkmkYU0Q60/dwmaBWdvyO7Sqe+bVq1dVb+//gbf7y48n/AN5vV5vNpvdbscp4/1+/7dkSdtut5IMFEX5opBHsoQiAJlEXGU6AlXy4XAYj8fD4XAsMBqNer3ebDZbLBZDgfl8/pWydChlV6uVVAOHG1TJWELqu8BgMGi325VKhRjB4/H4zHxUxhu/IrsX2ApwPZ1OL8f2STmXy2UymWKx2Gw2kcVnvV5PJpM8lsvlP3Qbn9VqtVQqQet0Osvlcjqd0rPJZELwXPwn5Ww222g0arUaJ/673S49LwlQDqn5557/m0zidDqdSCRk5YVCgeJpWyqVwg6fIJ/P59dkXtFZCSDLScG0nTkPBEjE2tw7/0SOxWJmszkUCjmdzmAw6Pf7o9EoXiwWi8fjsdls/X7/XvyxYXIZmBOCNGkpgCM5Lcamup5Go9FgMHBGIpFwOGy323U6nc/nI5dcO9VuS2UE2RO5G/wqXy6Xy4cANb8gUxLpTSYTalar1eVyaTQarGq12ng8zvs3AbfbrVo242WlWq0Wq0KfOOWesV5MLp/Pc6qOivbSan5FjdlI516vlyHLsqn/tTImHQ6HXq9nWlTOSnMl4BoIBJQbVBt2vV4/brgIEPD4IPiarNxBDvxX8JL8E1f3K69yWmydAAAAAElFTkSuQmCC')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/3.11.0/static/bec4223764b4b2866d68e2cf5fc417cc/8ae3e/font-weights.png",
                  "srcSet": ["/version/3.11.0/static/bec4223764b4b2866d68e2cf5fc417cc/a2ead/font-weights.png 259w", "/version/3.11.0/static/bec4223764b4b2866d68e2cf5fc417cc/6b9fd/font-weights.png 518w", "/version/3.11.0/static/bec4223764b4b2866d68e2cf5fc417cc/8ae3e/font-weights.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}></div>{`
  `}</div>
</div>
<h3>{`Italic`}</h3>
<p>{`Each weight has an italic style, which should only be used when you need to emphasize certain words in a sentence (titles of works, technical terms, names of devices, captions, etc.).`}</p>
<div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "118.91891891891892%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAYCAIAAAB1KUohAAAACXBIWXMAAAsTAAALEwEAmpwYAAAChElEQVQ4y6WUWU+iUQyG+f9/ALggxgQDgbAECAhRLghhCyC7IJuy7zhsMwgO8/j1GwYcNZNMLw49PW3f9m0/NN//QzTy80ORU0X0fwp+VmS5XH5TZLFYrFar+XyOjvGr4M1mg4cEc06nU4khWIyflaAGg7NUZL1ec5VgjFLFZ+AasuLU6/W63e5gMBgOh+hPT0+TyWQ0GvX7fezoXyEDiMIpJWwUOUXm+jEyz+12u1AolMtloKrVKkq9XqcK9IeHh0qlMpvNttvtuxQqMgjj8VhMAihoUgi0YSf4HXNvyLgmEgnSg3N/f5/P5yEc8GKxmMlkwMeIQzqdRiHFGTKt4lGr1YjvdDqPj4+gwVar1eI6+i10hNtZMMi4glZRBECohmF65loqlRgBWXBA55XhH5tXCUulUrFYLJlM3t3d4QcgkRi5EtloNCg7l8vRBSz8CZaymS2dQwy0y4QB5IRkgqmWV9rhtdlsAibMaUhDJR6P5+bm5vb21ul0ortcrmg0Gg6HfT6f2+3OKGK326+vr3ki18vLC+DqqMCUDQdhrQjpd7sdVeAKPvwTIAUfp63BRIUGg8Fms5nN5kgkYjQaTSaTxWJhMMFgkFouLy+9Xq+swFmw9MyGkFt2WBTQKAd8XrlipJCfiry+vqo9g8wAr66unIqAoNVqHQ4H+H6/n1qsVqter6dn+tfpdBcXFxjhksC3UYHAANlhmOTEjzlRMwNjc9gzdMbB/jFFPgFWDWoIVNmGw1AolM1mmTbx6PF4HG6ZDbSjBAIBnvb7/eFwOCsbPikShtyKsCoWRSgeECJRmBMjhILj96sSRprDiUh6EUg6XlE+/gOU2UpWOY+W4/Xv/4NfLBUUGKIOf3UAAAAASUVORK5CYII=')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/3.11.0/static/5d49086f312daf75b93249cbc352db2e/8ae3e/font-italic.png",
                  "srcSet": ["/version/3.11.0/static/5d49086f312daf75b93249cbc352db2e/a2ead/font-italic.png 259w", "/version/3.11.0/static/5d49086f312daf75b93249cbc352db2e/6b9fd/font-italic.png 518w", "/version/3.11.0/static/5d49086f312daf75b93249cbc352db2e/8ae3e/font-italic.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}></div>{`
  `}</div>
</div>
<h3>{`Color`}</h3>
<p>{`Type color should be carefully considered, with legibility and accessibility as your main concerns. Color can be helpful for reinforcing the meaning or context of a piece of text or action, but color should never be the only way this is communicated, or just for decoration. When text is generally kept neutral in color, then it can be much more powerful when you do need to use it to point out actions or a special important messages. The text itself is the clearest way to explain what the user needs to know, but icons can also help provide helpful clues.`}</p>
<div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA0klEQVQoz62SyQ6CQBBE/f+/I+HCvoR9HfZAgvqUCGhMjEofSHVNV9V0htPljzodID7fawVv2z2/iff918l935umaVlWnueapsVxDHYcx/d9QFEUSZIsjOd5fKdpekpeJuq6BmCBvixLIUQYhpDgNE2zLIPhdJ7nTdx1nW3buq4zgZhwLkIbBIFhGOSDscOdyf2aN/E4jnhXVUVI0zTiUcMwwBO4HJHZtu3rzrhKkkRgFEVsJcuyoiiu69KSTKyqqrQMwK93Puid1/r4zkf/Yb/VFY1e88jSeExaAAAAAElFTkSuQmCC')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/3.11.0/static/22ef0cab587b26ed7b82e42453918d3d/8ae3e/text-color-neutral-1.png",
                  "srcSet": ["/version/3.11.0/static/22ef0cab587b26ed7b82e42453918d3d/a2ead/text-color-neutral-1.png 259w", "/version/3.11.0/static/22ef0cab587b26ed7b82e42453918d3d/6b9fd/text-color-neutral-1.png 518w", "/version/3.11.0/static/22ef0cab587b26ed7b82e42453918d3d/8ae3e/text-color-neutral-1.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            Text should generally be kept neutral in color until there's a
            reason to use a color.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABWUlEQVQoz7VSa2/BYBT2W5Zo3e2OalWLTilazCxZluxfjJLYjLoPRbLsz+7RV7F+2IdlS05OnnN9znvO6/KGW78W1x8U+063sgdHZts2LRDeyrdiBOhg0+1r0CGdCjQhbn+DCup0ULfAzkOcwIdiZFzEXosVs1BeJlLDcm0tZicFbZlTF1J+pmgLVjCS0kjRlnl1kVXe8+o8fNXxhPQ9c4sVDUEaR9geKw44cSDejONJg+H7aXka5XrAfGbEp4fwIOo/a3tCNvMl0wWDertKpIdxwdBqK0xRqpoZeVqsmpIyQwjt0B2cJ97nw9gYAC6GN2J8P8K9gRyAyHn0hUka6BhNINTjhMF1vEs4d8VYA7o+PH2CMCVPwHP/+FGtb+TiXMyOSxUTtOW7da40R0KlvsHMzlPR1hqJpgK6BYjGnpvkBCThp0/iuLPtae8P7izGM45f4jD/63t+ARXR6oJ5DNCGAAAAAElFTkSuQmCC')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/3.11.0/static/0c46197cad0f329289b9e1d1d97bb507/8ae3e/text-color-neutral-2.png",
                  "srcSet": ["/version/3.11.0/static/0c46197cad0f329289b9e1d1d97bb507/a2ead/text-color-neutral-2.png 259w", "/version/3.11.0/static/0c46197cad0f329289b9e1d1d97bb507/6b9fd/text-color-neutral-2.png 518w", "/version/3.11.0/static/0c46197cad0f329289b9e1d1d97bb507/8ae3e/text-color-neutral-2.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`On dark backgrounds, neutral text appears as white.`}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA5klEQVQoz7VS2W7CMBDk//8NqS29oEDp6TjxWRt77YDppKkQ6UMfOFYja7Xa2dnDo90JNjoHuXQopez9Xe/2kUsp0xfplZJzaV+Nrxxg3619s2al5ZMo2/IfuaXWN95zF2QgS3iBqCOKogrIh9jkzYAcdeA3TC0VUtkVE4+NftZkO7KYNmIqoO8qx295fVd/jj+ST/1qfpRjG00MKpCh5BI40AeSo7zOvkZTPqqABHSXQ/47M2pjyOahhr6YiU7knlfXzDHHJxXiCKqFNC8miPXvRY7Y9uHxhuQydPbHL4Pjn/eHHWXfXE36woNFpkEAAAAASUVORK5CYII=')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/3.11.0/static/0b9be4f47292b467fb92cb118ecffeeb/8ae3e/text-color-decoration.png",
                  "srcSet": ["/version/3.11.0/static/0b9be4f47292b467fb92cb118ecffeeb/a2ead/text-color-decoration.png 259w", "/version/3.11.0/static/0b9be4f47292b467fb92cb118ecffeeb/6b9fd/text-color-decoration.png 518w", "/version/3.11.0/static/0b9be4f47292b467fb92cb118ecffeeb/8ae3e/text-color-decoration.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-incorrect"
                }}>{`Incorrect`}</p>{`
          `}<p parentName="figcaption">{`Don't use color as decoration.`}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA0ElEQVQoz71S7WrCQBD0/V+rtFCsiAq1ikhbP6o2uSSnuSTnzlw3BTGQ/qmKy7IMyw0zw14nXFGdm5DZah0ncJ68qTIrI9EjTA/7Gd0C2RiHOd1K4me4JbJX2AkO70iHtJOWshTIP5WmBFYxFJQ7+oxuzeKr7nxBn9AtWUVN57/K3sCOkQ6Q9JUP+wY7VSxxV8yL6ovp6r4RvqmMUp2jWNf6xYbllj4NqChlgA8UtaZv/s6M/EO+H5COJHqqLWj4ZBBwvObO/Be5def7/LBL6gcrSv6BL/T60wAAAABJRU5ErkJggg==')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/3.11.0/static/659f8e2fdef998b1004871720de6265e/8ae3e/text-color-contrast.png",
                  "srcSet": ["/version/3.11.0/static/659f8e2fdef998b1004871720de6265e/a2ead/text-color-contrast.png 259w", "/version/3.11.0/static/659f8e2fdef998b1004871720de6265e/6b9fd/text-color-contrast.png 518w", "/version/3.11.0/static/659f8e2fdef998b1004871720de6265e/8ae3e/text-color-contrast.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-incorrect"
                }}>{`Incorrect`}</p>{`
          `}<p parentName="figcaption">{`
            The contrast between text and its background needs to be 4.5:1, or
            3:1 for large (18px) text.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA7UlEQVQoz61S226DMAztf0997D5jfe7+pdrTgrqKNPdAQiChLQlUwMI0VbxucGRFR7aPbdnZjAuwWSoefjD+1VbonADwst1azjtCgpQeY5/CgEmgrCtMy4VHuFOq0zpw3uaqgZeHdb9ijPHbfn9VKkAYhGjS1CMUGL+BJDAWecxuY1QKj6fq9fHjUVZrjA3A5273mmkmLWTmTItT3RjlGDXn8pYrS5WjmUWyhEiD6LT3YlrzOExiLvjh8K6rTNpLzIvh2le5I0gnxVWKCkqLMkeo+SLmFLlrZuKld+77fnie+/nOMc7IOKz0w/6Nb8E/8kAYDnI0AAAAAElFTkSuQmCC')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/3.11.0/static/fa75588b31848e18ed3ccf74478f8e6c/8ae3e/text-color-meaning.png",
                  "srcSet": ["/version/3.11.0/static/fa75588b31848e18ed3ccf74478f8e6c/a2ead/text-color-meaning.png 259w", "/version/3.11.0/static/fa75588b31848e18ed3ccf74478f8e6c/6b9fd/text-color-meaning.png 518w", "/version/3.11.0/static/fa75588b31848e18ed3ccf74478f8e6c/8ae3e/text-color-meaning.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            Appropriate color usage can help reinforce the meaning of text, but
            is not a replacement for clear text and other helpful visual aids
            like icons.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAAArElEQVQoz62RvRKDIBCEfXFLn8OaB4LKRhD/RmWA2iRfcmlSRtyCOZZbdg+qZwGqUvHjEu5wNsbUdT1N0ziO67p676211DAhBGH2fT+OY1mWbduccznnr7jv+7ZthaWVLd3zPHddx0oNj5gjuV1rHWO8KXbTNJiQcxgGfFJKOFBLbCBDEQpSbHmzt5hgSilioxcNIzEeraRFBs/WfUD9Iy795/M8//3kOx7sMl6wQ/ExtTki6gAAAABJRU5ErkJggg==')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/3.11.0/static/91ea4ab5aeee7b904fee167950078938/8ae3e/text-color-meaning-2.png",
                  "srcSet": ["/version/3.11.0/static/91ea4ab5aeee7b904fee167950078938/a2ead/text-color-meaning-2.png 259w", "/version/3.11.0/static/91ea4ab5aeee7b904fee167950078938/6b9fd/text-color-meaning-2.png 518w", "/version/3.11.0/static/91ea4ab5aeee7b904fee167950078938/8ae3e/text-color-meaning-2.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
        `}<figcaption parentName="figure">{`
          `}<p parentName="figcaption" {...{
                  "className": "title title-correct"
                }}>{`Correct`}</p>{`
          `}<p parentName="figcaption">{`
            A message is successful if you can remove the color from it, and the
            meaning is still clear.
          `}</p>{`
        `}</figcaption>{`
      `}</figure>{`
    `}</div>{`
  `}</div>
</div>
<h4>{`Subdued`}</h4>
<p>{`It is common to lighten certain pieces of text in order to create more contrast. For example, making a subtitle more subdued than the title, or instructional text like the helper text on form inputs. This can be easily done by applying the "subdued" color to a piece of text. On a light background, this makes the color #707070. The inverse equivalent is a semi-transparent white.`}</p>
<div {...{
        "className": "two-col"
      }}>{`
  `}<div parentName="div" {...{
          "className": "row"
        }}>{`
    `}<div parentName="div" {...{
            "className": "col col-1"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABPUlEQVQoz61S3UqEUBDe664iCAxa6CmCHiSi2JbqAYIeqQcIKrYgAr3whxWNbhYCK/w5UqlHRcPf42qjQgjttiz1XQzfzJmPb5g5vapB2aAl37Fbn4le9QfU4izLXNeNoijP8yAI0jQNwzBJEkKI7/vt01yx4zg0TTMMo6qqLMuCIIiiKEmSoii6rvM8z3EcGMwWF0UBnnEcQ0fLSQNIIUIKxbnOMCeY27aNMYaxPc8D4nueg7HtYsuy0jRbemHlZ0ImRs2mJey9+rH2XvckXbRVgnBJiuVOVeTEpx+9m7F7weLze/9Oci/5cPxU/i5uPcN355XaR+sD1D9G1NBYOzBWdtXt07TuWCQO3uznzaG2cahtnWj9I5UaaKt7LztnCUjLRWPDlT6YB3TNmiPOHPHmrYCuWFucTP/xe34BIVjdOx3NjVcAAAAASUVORK5CYII=')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/3.11.0/static/733e31641bc2732d9bcbd19893f8090c/8ae3e/subdued-example-1.png",
                  "srcSet": ["/version/3.11.0/static/733e31641bc2732d9bcbd19893f8090c/a2ead/subdued-example-1.png 259w", "/version/3.11.0/static/733e31641bc2732d9bcbd19893f8090c/6b9fd/subdued-example-1.png 518w", "/version/3.11.0/static/733e31641bc2732d9bcbd19893f8090c/8ae3e/subdued-example-1.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
    `}<div parentName="div" {...{
            "className": "col col-2"
          }}>{`
      `}<figure parentName="div">{`
        `}<span parentName="figure" {...{
                "className": "gatsby-resp-image-wrapper",
                "style": {
                  "position": "relative",
                  "display": "block",
                  "marginLeft": "auto",
                  "marginRight": "auto",
                  "maxWidth": "756px"
                }
              }}>{`
      `}<span parentName="span" {...{
                  "className": "gatsby-resp-image-background-image",
                  "style": {
                    "paddingBottom": "66.02316602316603%",
                    "position": "relative",
                    "bottom": "0",
                    "left": "0",
                    "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAANCAIAAAAmMtkJAAAACXBIWXMAAAsTAAALEwEAmpwYAAABH0lEQVQoz6VSv0vDQBgN3R1dxT/CtSiI4FJLB1cXcXYLQgni6P/h4H/jIqIZWhPshRCSmFy8XC/3w3dNhy6NKT6++zhI3nvf+xLHGKM3YHaBY/4BSy6KIoqiJEkIIUKI3ciUUjDzPI/jmHMupWxWWG4HnlqyUooxBnKaplmWgY/eaqktwGrquoaH095gXpalPeiUIkhVVR3OoEBlPTYMQfj+oZiizQNR1Yl1ZiUawXhDWf36qZbCNMqW7rcwvAYdZODvodQaq5A9tw0aeXgKjm7Ds2k4dMOTO1Rw7JazhdVVXQM4+KyzyXQxGJPDm+jg+mvvkuxfzQfnycvbiqz+cA4enz9OXX987488/8KzfeQV8x7OOAjJjK43ipte//kvByHhLEKF4G8AAAAASUVORK5CYII=')",
                    "backgroundSize": "cover",
                    "display": "block"
                  }
                }}></span>{`
  `}<img parentName="span" {...{
                  "className": "gatsby-resp-image-image",
                  "alt": "",
                  "title": "",
                  "src": "/version/3.11.0/static/9a1972237695d7ba77cdbf42fbe31ca7/8ae3e/subdued-example-2.png",
                  "srcSet": ["/version/3.11.0/static/9a1972237695d7ba77cdbf42fbe31ca7/a2ead/subdued-example-2.png 259w", "/version/3.11.0/static/9a1972237695d7ba77cdbf42fbe31ca7/6b9fd/subdued-example-2.png 518w", "/version/3.11.0/static/9a1972237695d7ba77cdbf42fbe31ca7/8ae3e/subdued-example-2.png 756w"],
                  "sizes": "(max-width: 756px) 100vw, 756px",
                  "style": {
                    "width": "100%",
                    "height": "100%",
                    "margin": "0",
                    "verticalAlign": "middle",
                    "position": "absolute",
                    "top": "0",
                    "left": "0"
                  },
                  "loading": "lazy"
                }}></img>{`
    `}</span>{`
      `}</figure>{`
    `}</div>{`
  `}</div>
</div>
<hr></hr>
<h2>{`Margins`}</h2>
<p>{`Headings and paragraphs have margins built into them by default. These margins provide the desired rhythm created by spacing when they are used together in a traditional narrative context. These margins can be easily modified using the spacing scale or completely removed when using headings or paragraphs in other contexts.`}</p>
<figure>{`
  `}<span parentName="figure" {...{
          "className": "gatsby-resp-image-wrapper",
          "style": {
            "position": "relative",
            "display": "block",
            "marginLeft": "auto",
            "marginRight": "auto",
            "maxWidth": "1035px"
          }
        }}>{`
      `}<span parentName="span" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "101.15830115830116%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAIAAAAC64paAAAACXBIWXMAAAsTAAALEwEAmpwYAAACNUlEQVQ4y5XSC1PaQBAAYP//7+jLR2c6bRUfrVIfoIBWICEkMQgECTEJ5HFJ7nKX3UYFR2cEpzvfZXYm2dnc7a3hQ6SIEQJBJAgR5iHmwUMuQmRTzGav+TlnnIuibA0A7ieaPZbvBk173PFsdebokd+3zHY4u0WYAndQuC94gtMs4/NisytJp2W1VmmflvXGuaXKQ+lv7aA0VtpIfJg56LuvCP74v7iGCNj6jWfreP4VK1tY2cSzDTz+XDzh+AscfYDyx5fwzyfuTdKnzsUaGJomtdT29ainG4o8NDRkSZ4SoHGRvAHy585IB9VQ/hEpO0QpBfJP0i2heYLD47eZJxnxUpo9FgPw1gk7/cbPt1nle1b9KS62oV5aBhu7InDZ02kX626o3CgXff3KUOs9tWHfKSimyL1lcsGEEIsDU35hfR2vtvByCxuFTahvLLFefMCmFknofFSWofSl+qh7PexcJc4QiYuRswKIDJ5HBYNqruyI7m6u7qG2DyuhfsCCe5Kk8852r2PKdVtvFibaNfhjDKylQivPUr7YM/LbKpV3mLLH1YOsu4/G4Sq9oyx045TORzXVLzzp0JPLkX6Gdhut5iqTVhb7Sbo4MN/rhdObmauy2ERxj9xeLRd0MSoA4kihVQvGNRHIGKtIuqvEqmARW9xtSGda7DQTTwKiYay/5ybPYr64YUBcJbIvs6CDSfFOe0eicxqllM33nPh94iksMJCOkJrvYCNGI/pUjP8f8BhF8g83fVAFPHU5ywAAAABJRU5ErkJggg==')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<img parentName="span" {...{
            "className": "gatsby-resp-image-image",
            "alt": "",
            "title": "",
            "src": "/version/3.11.0/static/6ec30449e0bd83adaf0324c12b49592c/e3189/margins-illustration.png",
            "srcSet": ["/version/3.11.0/static/6ec30449e0bd83adaf0324c12b49592c/a2ead/margins-illustration.png 259w", "/version/3.11.0/static/6ec30449e0bd83adaf0324c12b49592c/6b9fd/margins-illustration.png 518w", "/version/3.11.0/static/6ec30449e0bd83adaf0324c12b49592c/e3189/margins-illustration.png 1035w", "/version/3.11.0/static/6ec30449e0bd83adaf0324c12b49592c/f793b/margins-illustration.png 1404w"],
            "sizes": "(max-width: 1035px) 100vw, 1035px",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "top": "0",
              "left": "0"
            },
            "loading": "lazy"
          }}></img>{`
    `}</span>
</figure>
</PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      